<template>
  <PageView class="with-submenu">
    <SubMenu
      :aria-label="$t('menu.aria-sub-menu')"
      :paths="subMenuPaths"
      :root="$t('titles.applications-page')"
      role="navigation"
    />
    <h1 class="title main-title">
      {{ $t("titles.authorization-application-creator-management") }}
    </h1>
    <div class="contentView">
      <LoadingAnimate
        v-if="isLoading"
        :size="'is-large'"
      />
      <b-table
        v-else
        :aria-current-label="$t('menu.aria-curent-page')"
        :aria-label="$t('menu.aria-pagination')"
        :aria-next-label="$t('menu.aria-next-page')"
        :aria-previous-label="$t('menu.aria-previous-page')"
        :current-page="currentPage"
        :data="listUsersAuth"
        :paginated="listUsersAuth.length > 20"
        :total="listUsersAuth.length"
        class="row tableAuth"
      >
        <b-table-column
          :aria-label="$t('dataTypeAuthorizations.users')"
          :custom-search="search"
          :label="$t('dataTypeAuthorizations.users')"
          field="login"
          searchable
          sortable
        >
          <template #searchable="{ filters, column }">
            <b-input
              v-model="filters[column.field]"
              :aria-label="$t('dataTypeAuthorizations.search')"
              :placeholder="$t('dataTypeAuthorizations.search')"
              icon="search"
              size="is-normal"
            />
          </template>
          <template #default="{ row }">
            <p :aria-label="row.login">
              {{ row.login }}
            </p>
          </template>
        </b-table-column>
        <b-table-column
          v-if="currentUser.openAdomAdmin"
          v-slot="props"
          :aria-label="$t('dataTypeAuthorizations.status-auth-application-creator')"
          :label="$t('dataTypeAuthorizations.status-auth-application-creator')"
          field="openAdomAdmin"
          sortable
        >
          <b-checkbox
            v-model="props.row.openAdomAdmin"
            :aria-label="$t('dataTypeAuthorizations.status-auth-application-creator') +': '+ props.row.openAdomAdmin"
            @update:model-value="selectAdmin(props.row.openAdomAdmin, props.row)"
          />
        </b-table-column>
        <b-table-column
          v-if="currentUser.authorizedForApplicationCreation || currentUser.openAdomAdmin"
          v-slot="props"
          :aria-label="$t('dataTypeAuthorizations.pattern-application-name')"
          :label="$t('dataTypeAuthorizations.pattern-application-name')"
          field="authorizations"
          sortable
        >
          <b-taginput
            v-model="props.row.authorizations"
            :aria-label="props.row.openAdomAdmin
              ? $t('dataTypeAuthorizations.authorization-user-creator-management')
              : $t('dataTypeAuthorizations.add-application-name')"
            :before-adding="beforeAdding"
            :close-type="props.row.openAdomAdmin ? 'is-light' : 'is-danger'"
            :disabled="props.row.openAdomAdmin"
            :icon="props.row.openAdomAdmin ? 'user-shield' : 'file'"
            :placeholder="
              props.row.openAdomAdmin
                ? $t('dataTypeAuthorizations.authorization-user-creator-management')
                : $t('dataTypeAuthorizations.add-application-name')
            "
            :type="props.row.openAdomAdmin ? 'is-light' : 'is-dark'"
            aria-close-label="Supprimer l'application"
            class="inputAuth"
            closable
            ellipsis
            @add="addApplication($event, props.row)"
            @close="removeApplication($event, props.row)"
            @remove="removeApplication($event, props.row)"
          />
        </b-table-column>
      </b-table>
      <div class="row">
        <div class="column is-offset-10 is-2">
          <b-button
            expanded
            icon-left="floppy-disk"
            icon-pack="far"
            rounded
            type="is-dark is-right"
            @click="registerChanges"
          />
        </div>
      </div>
    </div>
  </PageView>
</template>

<script lang="ts">
import SubMenu from "@/components/common/SubMenu.vue";
import PageView from "../../common/PageView.vue";
import { SubMenuPath } from "@/types/ui/SubMenuPath";
import LoadingAnimate from "@/components/common/LoadingAnimate.vue";
import { onMounted } from "vue";
import useArray from "@/composable/components/array";
import useObject from "@/composable/components/object";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import services from "@/composable/services";
import "@/style/views/authorization/gestion_app/AuthorizationsManagementForApplicationCreatorView.scss";
import { BButton, BCheckbox, BInput, BTable, BTableColumn, BTaginput } from "buefy";
import useBoolean from "@/composable/components/boolean";
import type { LoginAdminResult } from "@/types/generated/UsedGeneratedTypes";

interface ApplicationChanges {
  add?: string[];
  remove?: string[];
}

interface AuthChanges {
  administrator: { add: string[]; remove: string[] };
  applications: Record<string, ApplicationChanges>;
}

export default {
  name: "AuthorizationsManagementForApplicationCreatorView",
  components: {
    BButton,
    BCheckbox,
    BTable,
    BInput,
    BTableColumn,
    BTaginput,
    LoadingAnimate,
    PageView,
    SubMenu,
  },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const { shallowRefArray: subMenuPaths, doChangeArray: changeSubMenuPaths } = useArray();
    const { shallowRefArray: listUsersAuth, doChangeArray: changeListUsersAuth } = useArray();
    const { reactiveObject: changes, doChangeObject: changeChanges } = useObject<AuthChanges>({
      administrator: { add: [], remove: [] },
      applications: {},
    });
    let totalRows = -1;
    let currentPage = 1;
    const { refBoolean: isLoading, doChangeBoolean: changeIsLoading } = useBoolean(false);
    const currentUser = services.loginService.getAuthenticatedUser();

    onMounted(async () => {
      await init();
      changeSubMenuPaths([
        new SubMenuPath(
          t("titles.authorization-application-creator-management").toLowerCase(),
          () => router.push("/authorizationsManagementForApplicationCreator"),
          () => router.push("/applications"),
        ),
      ]);
    });

    function search(user: LoginAdminResult, search: string | number): boolean {
      return !!user.login.match(String(search));
    }

    async function init() {
      changeIsLoading(true);
      changeChanges({
        administrator: { add: [], remove: [] },
        applications: {},
      } as AuthChanges);
      if (Object.keys(listUsersAuth.value).length === 0) {
        changeListUsersAuth(
          await services.authorizationService.getAdminAuthorizationsForOpenAdom() as unknown as LoginAdminResult[],
        );
      }
      changeIsLoading(false);
    }

    async function registerChanges() {
      isLoading.value = true;
      await makeChanges();
      await init();
      isLoading.value = false;
    }

    async function makeChanges() {
      let localeListUsersAuth = listUsersAuth.value;
      if (Object.keys(changes.applications).length !== 0) {
        for (const userId in changes.applications) {
          if (changes.applications[userId].add) {
            for (const pattern of changes.applications[userId].add!) {
              services.authorizationService
                .createAuthorizedRole("applicationCreator", userId, pattern)
                .then((response) => {
                  if (response.id) {
                    localeListUsersAuth
                      .filter(user => user.login === response.login)
                      .forEach(user => (user.authorizations = response.authorizations));
                  }
                })
                .catch((error) => {
                  console.error(
                    `Erreur lors de la création du rôle pour ${pattern}:`,
                    error,
                  );
                });
            }
          }
        }
        for (const userId in changes.applications) {
          if (changes.applications[userId].remove) {
            for (const pattern of changes.applications[userId].remove!) {
              services.authorizationService
                .revokeAuthorizedRole(
                  "applicationCreator",
                  userId,
                  pattern,
                )
                .then((response) => {
                  if (response) {
                    localeListUsersAuth.filter(user => user.id === userId)[0].authorizations
                      = localeListUsersAuth
                        .filter(user => user.id === userId)[0]
                        .authorizations.filter(
                          (p: string) => !changes.applications[userId]?.remove?.includes(p),
                        );
                  }
                })
                .catch((error) => {
                  console.error(
                    `Erreur lors de la suppression du rôle pour ${pattern}:`,
                    error,
                  );
                });
            }
          }
        }
        changeListUsersAuth(localeListUsersAuth);
      }
      else if (changes.administrator.add.length !== 0) {
        changes.administrator.add.forEach((userId) => {
          localeListUsersAuth.filter(user => user.id === userId)[0].openAdomAdmin = true;
          localeListUsersAuth.filter(user => user.id === userId)[0].authorizations = [];
          localeListUsersAuth.filter(user => user.id === userId)[0].authorizations.push(".*");
          services.authorizationService.createAuthorizedRole("openAdomAdmin", userId, ".*");
        });
      }
      else if (changes.administrator.remove.length !== 0) {
        changes.administrator.remove.forEach((userId) => {
          localeListUsersAuth.filter(user => user.id === userId)[0].openAdomAdmin = false;
          localeListUsersAuth.filter(user => user.id === userId)[0].authorizations = [];
          services.authorizationService.revokeAuthorizedRole("openAdomAdmin", userId, ".*");
        });
      }
    }

    function selectAdmin(isAdmin: boolean, user: LoginAdminResult) {
      if (isAdmin) {
        if (changes.administrator.remove.find(v => v === user.id)) {
          changes.administrator.remove = changes.administrator.remove.filter(v => v !== user.id);
        }
        else if (!changes.administrator.add.find(v => v === user.id)) {
          changes.administrator.add.push(user.id);
        }
      }
      else {
        if (changes.administrator.add.find(v => v === user.id)) {
          changes.administrator.add = changes.administrator.add.filter(v => v !== user.id);
        }
        else if (!changes.administrator.remove.find(v => v === user.id)) {
          changes.administrator.remove.push(user.id);
        }
      }
    }

    function addApplication(value: string, user: LoginAdminResult) {
      if (changes.applications[user.id]?.remove?.find((v: string) => v === value)) {
        changes.applications[user.id].remove = changes.applications[user.id].remove!.filter(
          (v: string) => v !== value,
        );
      }
      else {
        changes.applications[user.id] = changes.applications[user.id] || {};
        changes.applications[user.id].add = changes.applications[user.id].add || [];
        changes.applications[user.id].add!.push(value);
      }
    }

    function removeApplication(value: string, user: LoginAdminResult) {
      if (changes.applications[user.id]?.add?.find((v: string) => v === value)) {
        changes.applications[user.id].add = changes.applications[user.id].add!.filter(
          (v: string) => v !== value,
        );
      }
      else {
        changes.applications[user.id] = changes.applications[user.id] || {};
        changes.applications[user.id].remove = changes.applications[user.id].remove || [];
        changes.applications[user.id].remove!.push(value);
      }
    }

    function beforeAdding(value: string): boolean {
      return !!value;
    }

    return {
      subMenuPaths,
      listUsersAuth,
      totalRows,
      currentPage,
      isLoading,
      search,
      changes,
      currentUser,
      registerChanges,
      makeChanges,
      selectAdmin,
      addApplication,
      removeApplication,
      beforeAdding,
    };
  },
};
</script>
